import { Animation, Container3D, glTFAsset, Mesh3D, Model, Quaternion, ShadowCastingLight } from "pixi3d/pixi7";
import { Container3DExtentions } from "../../../../../extensions/Container3DExtentions";
import { DiceResult } from "../../models/DiceResult";
import { Dice } from "./Dice";
import { Renderer } from "pixi.js";
import { TransparentShadowReceiverMaterial } from "./groundMaterial/TransparentShadowReceiverMaterial";
import { IAnimationIndexProvider } from "./animationIndexProviders/IAnimationIndexProvider";

export class AnimatedDices extends Container3D {
    public static readonly ANIMATION_NAME_TEMPLATE: RegExp = /Animation_([0-9]+)_Dice_([0-9]+)_Result_([0-9]+)/gm;
    private readonly dicesModel: Model;
    private readonly animations: Array<DiceAnimationTuple>;
    private readonly dices: Array<Dice>;
    private readonly animationIndexProvider: IAnimationIndexProvider;

    public constructor(
        animatedDicesAsset: glTFAsset,
        renderer: Renderer,
        shadowCastingLight: ShadowCastingLight,
        animationIndexProvider: IAnimationIndexProvider
    ) {
        super();
        this.dicesModel = Model.from(animatedDicesAsset);
        this.dices = this.createDices(renderer, shadowCastingLight);
        this.animations = this.parseDicesAnimations();
        this.addChild(...this.dices);
        const ground = this.configure(renderer, shadowCastingLight);
        this.addChild(ground);
        this.animationIndexProvider = animationIndexProvider;
    }

    public async throwDices(diceResults: Array<DiceResult>): Promise<void> {
        const simulation: DiceAnimationTuple = this.animations[this.animationIndexProvider.getAnimationIndex()];
        return await this.throwDicesInternal(simulation, diceResults);
    }

    public async collectDices(): Promise<void> {
        await this.collectDicesInternal();
    }

    private async throwDicesInternal(simulation: DiceAnimationTuple, requestedDiceResults: Array<DiceResult>): Promise<void> {
        const dicesAnimations = new Array<Promise<void>>();
        for (let i = 0; i < 3; i++) {
            const requestedDiceResult = requestedDiceResults[i];
            const dice = this.dices[i];
            dice.insureResult(requestedDiceResult, simulation.resultForDice[i]);
            dicesAnimations.push(dice.throwDice(simulation.animationForDice[i]));
        }
        await Promise.all(dicesAnimations);
    }

    private async collectDicesInternal(): Promise<void> {
        const dicesCollectAnimations = new Array<Promise<void>>();
        for (let dice of this.dices) {
            dicesCollectAnimations.push(dice.collectDice());
        }
        await Promise.all(dicesCollectAnimations);
    }

    private createDices(renderer: Renderer, shadowCastingLight: ShadowCastingLight): Array<Dice> {
        const dices = new Array<Dice>();
        const pipeline = renderer.plugins.pipeline;
        for (let i = 1; i <= 3; i++) {
            const index = i.toString();
            const diceContainer = Container3DExtentions.findByName(this.dicesModel, `DiceContainer_${index}`);
            const dice = new Dice(diceContainer, `Cube.00${index}`, `DicePrefab_${index}`);
            pipeline.enableShadows(dice.mesh, shadowCastingLight);
            dices.push(dice);
        }
        return dices;
    }

    private parseDicesAnimations(): Array<DiceAnimationTuple> {
        if (this.dicesModel.animations.length % 3 != 0) {
            throw new Error("You have to configure the same amount of animations for each of three dices.");
        }
        const availableAnimationIndices = new Set<number>();
        for (const animation of this.dicesModel.animations) {
            const animationIndex = new RegExp(AnimatedDices.ANIMATION_NAME_TEMPLATE).exec(animation.name)[1];
            availableAnimationIndices.add(parseInt(animationIndex));
        }
        const amountOfAnimationsPerDice = this.dicesModel.animations.length / 3;
        if (amountOfAnimationsPerDice != availableAnimationIndices.size) {
            throw new Error("Amount of animations per dice doesn't correspond to available animation indices.");
        }
        const dicesAnimations = new Array<DiceAnimationTuple>();
        for (const animationIndex of availableAnimationIndices) {
            dicesAnimations.push(
                new DiceAnimationTuple(
                    this.resolveAnimation(`Animation_${animationIndex}_Dice_1`),
                    this.resolveAnimation(`Animation_${animationIndex}_Dice_2`),
                    this.resolveAnimation(`Animation_${animationIndex}_Dice_3`)
                )
            );
        }
        return dicesAnimations;
    }

    private resolveAnimation(animationName: string): Animation {
        const animation = this.dicesModel.animations.find(value => value.name.startsWith(animationName));
        if (animation == null) {
            throw new Error(`Animation with name ${animationName} not exist.`);
        }
        return animation;
    }

    private configure(renderer: Renderer, shadowCastingLight: ShadowCastingLight): Mesh3D {
        // Add fake ground 
        const ground = Mesh3D.createPlane(new TransparentShadowReceiverMaterial(shadowCastingLight));
        ground.position.set(0, 0, 0);
        ground.scale.set(0.6, 0.6, 0.6);
        const pipeline = renderer.plugins.pipeline;
        pipeline.enableShadows(ground, shadowCastingLight);
        return ground;
    }

}

class DiceAnimationTuple {
    public readonly animationForDice: Animation[];
    public readonly resultForDice: DiceResult[];

    public constructor(animationForDice1: Animation, animationForDice2: Animation, animationForDice3: Animation) {
        this.animationForDice = new Array<Animation>();
        this.animationForDice.push(animationForDice1);
        this.animationForDice.push(animationForDice2);
        this.animationForDice.push(animationForDice3);
        this.resultForDice = new Array<DiceResult>();
        this.resultForDice.push(this.resolveAnimationResult(animationForDice1));
        this.resultForDice.push(this.resolveAnimationResult(animationForDice2));
        this.resultForDice.push(this.resolveAnimationResult(animationForDice3));
    }

    private resolveAnimationResult(animation: Animation): DiceResult {
        const animationResult: number = parseInt(new RegExp(AnimatedDices.ANIMATION_NAME_TEMPLATE).exec(animation.name)[3]);
        return animationResult;
    }
}
